import { Stack, Typography, styled } from '@mui/material';

export const Widget = styled(Stack)(() => ({
  width: '100%',
  overflow: 'hidden',
  borderRadius: '1rem',
  backgroundColor: '#FEFEFE',
  boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
}));

export const WidgetHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  alignSelf: 'stretch',
  width: '100%',
  padding: '1rem 1.5rem',
  backgroundColor: '#FEFEFE',
  fontSize: '1.125rem',
  fontWeight: 500,
  lineHeight: '100%',
  color: theme.palette.grey[900],
}));

export const SpecsGrid = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  gap: '3rem',
  width: '100%',
  padding: '1rem 1.5rem 1.5rem 1.5rem',
}));

export const SpecsColumn = styled(Stack)(() => ({
  flex: '1 0 0',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1rem',
  minWidth: 0,
}));

export const SpecRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
  gap: '1rem',
}));

export const SpecLabel = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '130%',
  color: theme.palette.grey[800],
  whiteSpace: 'nowrap',
}));

export const SpecValue = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: '1rem',
  fontWeight: 500,
  lineHeight: '100%',
  color: theme.palette.grey[950],
  textAlign: 'right',
}));

export const SpecDivider = styled('div')(({ theme }) => ({
  height: 1,
  alignSelf: 'stretch',
  borderRadius: '6.25rem',
  backgroundColor: theme.palette.grey[100],
}));

export const WidgetBody = styled(Stack)(() => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1rem',
  width: '100%',
  padding: '0.5rem 1rem 1rem 1rem',
}));

export const LocationFramesRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '1rem',
  width: '100%',
}));

export const LocationFrame = styled(Stack)(({ theme }) => ({
  flex: '1 0 0',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '1.5rem',
  alignSelf: 'stretch',
  padding: '1rem',
  borderRadius: '1rem',
  border: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: '#FEFEFE',
}));

export const LocationFrameItem = styled(Stack)(() => ({
  flex: '1 0 0',
  flexDirection: 'column',
  gap: '0.5rem',
  minWidth: 0,
}));

export const LocationFieldsColumn = styled(Stack)(() => ({
  flex: '2 0 0',
  flexDirection: 'column',
  gap: '1rem',
  minWidth: 0,
}));

export const MapFrame = styled('div')(({ theme }) => ({
  flex: '3 0 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '0.625rem',
  padding: '2.125rem 0.9375rem',
  alignSelf: 'stretch',
  borderRadius: '1rem',
  border: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: '#FEFEFE',
  overflow: 'hidden',
}));

export const FieldValue = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: '1.375rem',
  fontWeight: 500,
  lineHeight: '120%',
  color: theme.palette.grey[800],
}));

export const FieldLabel = styled('p')(({ theme }) => ({
  margin: 0,
  alignSelf: 'stretch',
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: '100%',
  letterSpacing: '0.24px',
  textTransform: 'uppercase',
  color: theme.palette.grey[800],
}));

export const AudienceGrid = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 1fr)',
  columnGap: '2rem',
  rowGap: '1rem',
  width: '100%',
}));

export const AudienceLabel = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '100%',
  letterSpacing: '0.28px',
  textTransform: 'uppercase',
  color: theme.palette.grey[800],
}));

export const SegmentLabels = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  gap: '0.5rem',
}));

export const SegmentLabel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'flexBasis',
})<{ flexBasis: number }>(({ theme, flexBasis }) => ({
  flex: `${flexBasis} 0 0`,
  display: 'flex',
  justifyContent: 'center',
  gap: '0.25rem',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: '125%',
  color: theme.palette.grey[950],
  whiteSpace: 'nowrap',
}));

export const DemographicColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  width: '100%',
}));

export const SegmentedBar = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  height: '0.75rem',
}));

const BAR_SEGMENT_OVERLAP = '0.25rem';

export const BarSegment = styled('div', {
  shouldForwardProp: (prop) =>
    !['percent', 'segmentColor', 'isFirst', 'isLast', 'stackOrder'].includes(prop),
})<{
  percent: number;
  segmentColor: string;
  isFirst: boolean;
  isLast: boolean;
  stackOrder: number;
}>(({ percent, segmentColor, isFirst, isLast, stackOrder }) => ({
  flex: `0 0 ${isLast ? `${percent}%` : `calc(${percent}% + ${BAR_SEGMENT_OVERLAP})`}`,
  marginLeft: isFirst ? 0 : `-${BAR_SEGMENT_OVERLAP}`,
  zIndex: stackOrder,
  borderTopLeftRadius: isFirst ? '9999px' : 0,
  borderBottomLeftRadius: isFirst ? '9999px' : 0,
  borderTopRightRadius: '9999px',
  borderBottomRightRadius: '9999px',
  backgroundColor: segmentColor,
}));

export const ItemInfoBoxValue = styled(Typography)(({ theme }) => ({
  margin: 0,
  alignItems: 'center',
  color: theme.palette.grey[800],
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '150%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));
